import { FilterModel, SortModelItem } from 'ag-grid-community';
import {
    DataRequestListTabEnum,
    DataRequestSummarySortFieldEnum,
    NumberFilterOperatorEnum
} from '@vivli/features/data-requests/infrastructure/enum';
import { IDataRequestSummaryPageRequest } from '@vivli/features/data-requests/infrastructure/interface';
import { DataRequestFormatService } from '@vivli/features/data-requests/infrastructure/service';

// DEV-958: the column ids of the Data Requests grid. The paged grid maps them to the server's sort
// fields and filter parameters below; the Selenium page object (DataRequestPage.java) reads them too.
export const DataRequestColumnId = {
    doi: 'dataRequestDoi',
    title: 'requestTitle',
    studyCount: 'requestedStudyCount',
    leadResearcher: 'leadResearcher',
    requester: 'userName',
    status: 'status'
} as const;

// Grid column id -> the server's whitelisted sort field. The ID column sorts by DOI, which is also
// the default; Draft's hidden createdTime column sorts by creation.
const sortFieldByColId: Record<string, DataRequestSummarySortFieldEnum> = {
    [DataRequestColumnId.doi]: DataRequestSummarySortFieldEnum.DataRequestDoi,
    createdTime: DataRequestSummarySortFieldEnum.CreatedTime,
    updatedDate: DataRequestSummarySortFieldEnum.UpdatedDate,
    [DataRequestColumnId.title]: DataRequestSummarySortFieldEnum.RequestTitle,
    [DataRequestColumnId.studyCount]: DataRequestSummarySortFieldEnum.RequestedStudyCount,
    [DataRequestColumnId.leadResearcher]: DataRequestSummarySortFieldEnum.LeadResearcher,
    [DataRequestColumnId.requester]: DataRequestSummarySortFieldEnum.Requester,
    [DataRequestColumnId.status]: DataRequestSummarySortFieldEnum.Status
};

// ag-grid number filter type -> the server's operator. Only these three are offered on the column.
const operatorByFilterType: Record<string, NumberFilterOperatorEnum> = {
    equals: NumberFilterOperatorEnum.Equals,
    greaterThan: NumberFilterOperatorEnum.GreaterThan,
    lessThan: NumberFilterOperatorEnum.LessThan
};

export const getDataRequestServerSort = (colId?: string, sort?: 'asc' | 'desc' | null) => ({
    sortField: sortFieldByColId[colId] ?? DataRequestSummarySortFieldEnum.DataRequestDoi,
    sortDescending: sort !== 'asc'
});

const textFilterValue = (filterModel: FilterModel | undefined, colId: string): string | undefined => {
    const value = filterModel?.[colId]?.filter;
    return typeof value === 'string' && value.trim() ? value.trim() : undefined;
};

// DEV-958: the server-side form of the grid's column filters. Returns null when the Status filter
// text matches no status at all, so the caller can show an empty grid without a request: the
// server filters statuses by enum, and there is no enum for text that matches nothing.
export const toServerColumnFilters = (
    filterModel: FilterModel | undefined,
    isAmr: boolean,
    duaTranslation: string
): Partial<IDataRequestSummaryPageRequest> | null => {
    const filters: Partial<IDataRequestSummaryPageRequest> = {
        doiFilter: textFilterValue(filterModel, DataRequestColumnId.doi),
        titleFilter: textFilterValue(filterModel, DataRequestColumnId.title),
        requesterFilter: textFilterValue(filterModel, DataRequestColumnId.requester),
        leadResearcherFilter: textFilterValue(filterModel, DataRequestColumnId.leadResearcher)
    };

    const countFilter = filterModel?.[DataRequestColumnId.studyCount];
    if (typeof countFilter?.filter === 'number') {
        filters.studyCountFilter = countFilter.filter;
        filters.studyCountOperator = operatorByFilterType[countFilter.type] ?? NumberFilterOperatorEnum.Equals;
    }

    const statusText = textFilterValue(filterModel, DataRequestColumnId.status);
    if (statusText) {
        const statuses = DataRequestFormatService.statusesMatchingDisplayText(statusText, isAmr, duaTranslation);
        if (statuses.length === 0) {
            return null;
        }
        filters.statuses = statuses;
    }

    return filters;
};

export interface IDataRequestPageRequestInput {
    tab: DataRequestListTabEnum;
    startRow: number;
    endRow: number;
    search?: string;
    sortModel?: SortModelItem[];
    filterModel?: FilterModel;
    isAmr: boolean;
    duaTranslation: string;
}

// DEV-958: one page request from the grid's row window, sort and filters, shared by the datasource
// and the CSV export so both ask the server for the same rows. Null means "no rows can match".
export const buildDataRequestPageRequest = ({
    tab,
    startRow,
    endRow,
    search,
    sortModel,
    filterModel,
    isAmr,
    duaTranslation
}: IDataRequestPageRequestInput): IDataRequestSummaryPageRequest | null => {
    const filters = toServerColumnFilters(filterModel, isAmr, duaTranslation);
    if (!filters) {
        return null;
    }

    const sort = sortModel?.[0];
    const serverSort = getDataRequestServerSort(sort?.colId, sort?.sort);
    const statusOrder =
        serverSort.sortField === DataRequestSummarySortFieldEnum.Status
            ? DataRequestFormatService.statusesInDisplayOrder(isAmr, duaTranslation)
            : undefined;

    return {
        tab,
        startRow,
        endRow,
        search: search?.trim() || undefined,
        ...serverSort,
        ...filters,
        statusOrder
    };
};
